import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import { 
  MdFolder, 
  MdCheckCircle, 
  MdEventAvailable, 
  MdAssignment,
  MdAdd,
  MdTrendingUp,
  MdWarning,
  MdAccessTime,
  MdCalendarToday,
  MdEvent,
  MdLocationOn,
  MdExpandMore,
  MdExpandLess,
  MdOpenInNew
} from 'react-icons/md';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import './Dashboard.css';

// Expandable description component
const EventDescription = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const isLong = text.length > 120;
  return (
    <div className="event-desc-wrap">
      <p className={`event-top-desc ${!expanded && isLong ? 'clamped' : ''}`}>
        {text}
      </p>
      {isLong && (
        <button className="event-desc-toggle" onClick={() => setExpanded(e => !e)}>
          {expanded ? <><MdExpandLess /> Show less</> : <><MdExpandMore /> Read more</>}
        </button>
      )}
    </div>
  );
};
import './Dashboard.css';

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalProjects: 0,
    totalTasks: 0,
    completedTasks: 0,
    upcomingEvents: 0
  });
  const [recentTasks, setRecentTasks] = useState([]);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [chartData, setChartData] = useState({
    tasksByStatus: [],
    tasksByPriority: [],
    projectsData: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [projectsRes, tasksRes, eventsRes] = await Promise.all([
        axios.get('/api/projects'),
        axios.get('/api/tasks'),
        axios.get('/api/events')
      ]);

      const tasks = tasksRes.data;
      const projects = projectsRes.data;
      const events = eventsRes.data;

      // Upcoming events: sort by start_time, show future ones first (up to 5)
      const nowTs = new Date();
      const sortedEvents = [...events]
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
      const futureEvents = sortedEvents.filter(e => new Date(e.start_time) >= nowTs);
      setUpcomingEvents((futureEvents.length > 0 ? futureEvents : sortedEvents).slice(0, 5));
      
      // Filter tasks for regular users (only assigned to them)
      const userTasks = user?.role === 'admin' 
        ? tasks 
        : tasks.filter(t => t.assigned_to === user?.id);
      
      const completedCount = userTasks.filter(t => t.status === 'completed').length;
      const todoCount = userTasks.filter(t => t.status === 'todo').length;
      const inProgressCount = userTasks.filter(t => t.status === 'in_progress').length;

      // Priority distribution
      const highPriority = userTasks.filter(t => t.priority === 'high').length;
      const mediumPriority = userTasks.filter(t => t.priority === 'medium').length;
      const lowPriority = userTasks.filter(t => t.priority === 'low').length;

      // Upcoming deadlines (tasks due in next 7 days)
      const now = new Date();
      const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      const upcoming = userTasks
        .filter(t => t.due_date && new Date(t.due_date) <= nextWeek && t.status !== 'completed')
        .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
        .slice(0, 5);

      // Recent activity (last 10 tasks updated)
      const activity = userTasks
        .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
        .slice(0, 10)
        .map(task => ({
          id: task.id,
          type: 'task',
          action: task.status === 'completed' ? 'completed' : 'updated',
          title: task.title,
          project: task.project_name,
          time: task.updated_at || task.created_at
        }));

      // For regular users, show only their projects (projects with their tasks)
      const userProjectIds = [...new Set(userTasks.map(t => t.project_id).filter(Boolean))];
      const userProjects = user?.role === 'admin' 
        ? projects 
        : projects.filter(p => userProjectIds.includes(p.id));

      setStats({
        totalProjects: userProjects.length,
        totalTasks: userTasks.length,
        completedTasks: completedCount,
        upcomingEvents: events.length
      });

      setChartData({
        tasksByStatus: [
          { name: 'To Do', value: todoCount, color: '#6B7280' },
          { name: 'In Progress', value: inProgressCount, color: '#3B82F6' },
          { name: 'Completed', value: completedCount, color: '#10B981' }
        ],
        tasksByPriority: [
          { name: 'High', value: highPriority, color: '#EF4444' },
          { name: 'Medium', value: mediumPriority, color: '#F59E0B' },
          { name: 'Low', value: lowPriority, color: '#3B82F6' }
        ],
        projectsData: userProjects.slice(0, 5).map(p => ({
          name: p.name.length > 15 ? p.name.substring(0, 15) + '...' : p.name,
          tasks: user?.role === 'admin' 
            ? (p.task_count || 0)
            : userTasks.filter(t => t.project_id === p.id).length
        }))
      });

      setRecentTasks(userTasks.slice(0, 5));
      setUpcomingDeadlines(upcoming);
      setRecentActivity(activity);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const formatDueDate = (dueDate) => {
    if (!dueDate) return null;
    const date = new Date(dueDate);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date() && new Date(dueDate).toDateString() !== new Date().toDateString();
  };

  if (loading) {
    return (
      <div className="app-container">
        <Sidebar />
        <div className="main-content">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Sidebar />
      <div className="main-content dashboard-main">
        <div className="dashboard-header">
          <div>
            <h1>{user?.role === 'admin' ? 'Admin Dashboard' : 'My Dashboard'}</h1>
            <p>Welcome back, {user?.full_name || user?.username}!</p>
            {user?.role !== 'admin' && (
              <p className="user-subtitle">View and manage your assigned tasks</p>
            )}
          </div>
          {user?.role === 'admin' && (
            <div className="quick-actions">
              <button className="quick-btn" onClick={() => navigate('/tasks')}>
                <MdAdd /> New Task
              </button>
              <button className="quick-btn" onClick={() => navigate('/projects')}>
                <MdAdd /> New Project
              </button>
              <button className="quick-btn" onClick={() => navigate('/events')}>
                <MdAdd /> New Event
              </button>
            </div>
          )}
        </div>

        <div className="dashboard-content">

          {/* ── JiruuTech Website Banner ── */}
          <a
            href="https://jiruutech.com"
            target="_blank"
            rel="noopener noreferrer"
            className="jiruutech-banner"
          >
            <div className="jiruutech-banner-left">
              <span className="jiruutech-banner-label">Visit our website</span>
              <span className="jiruutech-banner-url">jiruutech.com</span>
            </div>
            <div className="jiruutech-banner-right">
              <span>Go to JiruuTech</span>
              <MdOpenInNew />
            </div>
          </a>

          {/* ── Upcoming Events — very top, full width ── */}
          {upcomingEvents.length > 0 && (
            <div className="widget-card events-top-widget">
              <div className="widget-header">
                <h3><MdEvent /> Upcoming Events</h3>
                <span className="badge">{upcomingEvents.length}</span>
              </div>
              <div className="events-top-grid">
                {upcomingEvents.map(event => (
                  <div key={event.id} className="event-top-card">
                    <div className="event-top-date">
                      <span className="event-widget-day">{new Date(event.start_time).getDate()}</span>
                      <span className="event-widget-month">
                        {new Date(event.start_time).toLocaleString('en-US', { month: 'short' })}
                      </span>
                    </div>
                    <div className="event-top-body">
                      <h4 className="event-top-title">{event.title}</h4>
                      <EventDescription text={event.description} />
                      <div className="event-widget-meta">
                        <span><MdAccessTime />
                          {new Date(event.start_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                          {' — '}
                          {new Date(event.end_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {event.location && <span><MdLocationOn /> {event.location}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Stats Grid */}
          <div className="stats-grid">
            <div className="stat-card" onClick={() => navigate('/projects')}>
              <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #3B82F6, #2563EB)' }}>
                <MdFolder />
              </div>
              <div className="stat-info">
                <h3>{stats.totalProjects}</h3>
                <p>{user?.role === 'admin' ? 'Total Projects' : 'My Projects'}</p>
              </div>
              <div className="stat-trend">
                <MdTrendingUp />
              </div>
            </div>

            <div className="stat-card" onClick={() => navigate('/tasks')}>
              <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}>
                <MdAssignment />
              </div>
              <div className="stat-info">
                <h3>{stats.totalTasks}</h3>
                <p>{user?.role === 'admin' ? 'Total Tasks' : 'Assigned Tasks'}</p>
              </div>
              <div className="stat-trend">
                <MdTrendingUp />
              </div>
            </div>

            <div className="stat-card" onClick={() => navigate('/tasks')}>
              <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)' }}>
                <MdCheckCircle />
              </div>
              <div className="stat-info">
                <h3>{stats.completedTasks}</h3>
                <p>{user?.role === 'admin' ? 'Completed' : 'Completed Tasks'}</p>
              </div>
              <div className="stat-trend success">
                <MdTrendingUp />
              </div>
            </div>

            <div className="stat-card" onClick={() => navigate('/events')}>
              <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #F59E0B, #D97706)' }}>
                <MdEventAvailable />
              </div>
              <div className="stat-info">
                <h3>{stats.upcomingEvents}</h3>
                <p>{user?.role === 'admin' ? 'Events' : 'Upcoming Events'}</p>
              </div>
              <div className="stat-trend">
                <MdTrendingUp />
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="charts-grid">
            <div className="chart-card">
              <h3>Tasks by Status</h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={chartData.tasksByStatus}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {chartData.tasksByStatus.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-card">
              <h3>Tasks by Priority</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData.tasksByPriority}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#10B981" radius={[8, 8, 0, 0]}>
                    {chartData.tasksByPriority.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-card">
              <h3>Tasks per Project</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData.projectsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="tasks" fill="#3B82F6" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Content Grid */}
          <div className="content-grid">
            {/* Upcoming Deadlines */}
            <div className="widget-card">
              <div className="widget-header">
                <h3><MdWarning /> {user?.role === 'admin' ? 'Upcoming Deadlines' : 'My Upcoming Deadlines'}</h3>
                <span className="badge">{upcomingDeadlines.length}</span>
              </div>
              {upcomingDeadlines.length === 0 ? (
                <p className="empty-state">{user?.role === 'admin' ? 'No upcoming deadlines' : 'No upcoming deadlines for your tasks'}</p>
              ) : (
                <div className="deadline-list">
                  {upcomingDeadlines.map(task => (
                    <div key={task.id} className={`deadline-item ${isOverdue(task.due_date) ? 'overdue' : ''}`}>
                      <div className="deadline-info">
                        <h4>{task.title}</h4>
                        <p>{task.project_name || 'No project'}</p>
                      </div>
                      <div className="deadline-date">
                        <MdCalendarToday />
                        <span>{formatDueDate(task.due_date)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Activity */}
            <div className="widget-card">
              <div className="widget-header">
                <h3><MdAccessTime /> {user?.role === 'admin' ? 'Recent Activity' : 'My Recent Activity'}</h3>
              </div>
              {recentActivity.length === 0 ? (
                <p className="empty-state">{user?.role === 'admin' ? 'No recent activity' : 'No recent activity on your tasks'}</p>
              ) : (
                <div className="activity-list">
                  {recentActivity.map((activity, index) => (
                    <div key={index} className="activity-item">
                      <div className={`activity-icon ${activity.action}`}>
                        {activity.action === 'completed' ? <MdCheckCircle /> : <MdAssignment />}
                      </div>
                      <div className="activity-info">
                        <h4>{activity.title}</h4>
                        <p>
                          <span className="activity-action">{activity.action}</span>
                          {activity.project && ` in ${activity.project}`}
                        </p>
                      </div>
                      <span className="activity-time">{formatTimeAgo(activity.time)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Tasks */}
            <div className="widget-card">
              <div className="widget-header">
                <h3><MdAssignment /> {user?.role === 'admin' ? 'Recent Tasks' : 'My Recent Tasks'}</h3>
              </div>
              {recentTasks.length === 0 ? (
                <p className="empty-state">{user?.role === 'admin' ? 'No tasks yet. Create your first task!' : 'No tasks assigned to you yet.'}</p>
              ) : (
                <div className="task-list">
                  {recentTasks.map(task => (
                    <div key={task.id} className="task-item">
                      <div className="task-status" data-status={task.status}></div>
                      <div className="task-content">
                        <h4>{task.title}</h4>
                        <p>{task.project_name || 'No project'}</p>
                      </div>
                      <span className={`task-priority priority-${task.priority}`}>
                        {task.priority}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
